import { forwardRef, useId, type InputHTMLAttributes, type ReactNode, type SelectHTMLAttributes, type TextareaHTMLAttributes } from 'react';
import { Icon } from './Icon';
import type { IconName } from './icons';

// Labelled form fields with help and error text wired up for screen readers.

interface FieldShellProps {
  label: ReactNode;
  /** Shown under the control in muted text. */
  help?: ReactNode;
  /** Shown under the control in red; also marks the control invalid. */
  error?: string | null;
  /** Marks the label with a red asterisk. */
  required?: boolean;
  /** Extra content on the label line, aligned right (a link or a badge). */
  aside?: ReactNode;
  className?: string;
}

export function Field({
  label,
  help,
  error,
  required,
  aside,
  className,
  children,
  htmlFor,
}: FieldShellProps & { children: ReactNode; htmlFor?: string }) {
  return (
    <div className={['field', className].filter(Boolean).join(' ')}>
      <div className="field-top">
        <label htmlFor={htmlFor}>
          {label}
          {required ? <span className="req"> *</span> : null}
        </label>
        {aside ? <span className="field-aside">{aside}</span> : null}
      </div>
      {children}
      {error ? (
        <p className="field-err" id={htmlFor ? `${htmlFor}-err` : undefined} role="alert">
          <Icon name="warn" className="i-xs" />
          {error}
        </p>
      ) : help ? (
        <p className="field-help" id={htmlFor ? `${htmlFor}-help` : undefined}>
          {help}
        </p>
      ) : null}
    </div>
  );
}

function describedBy(id: string, error?: string | null, help?: ReactNode): string | undefined {
  if (error) return `${id}-err`;
  if (help) return `${id}-help`;
  return undefined;
}

type InputFieldProps = FieldShellProps & Omit<InputHTMLAttributes<HTMLInputElement>, 'className'> & { icon?: IconName; inputClassName?: string };

export const InputField = forwardRef<HTMLInputElement, InputFieldProps>(function InputField(
  { label, help, error, required, aside, className, icon, inputClassName, id, ...rest },
  ref,
) {
  const auto = useId();
  const fid = id ?? auto;
  const input = (
    <input
      ref={ref}
      id={fid}
      className={['input', inputClassName].filter(Boolean).join(' ')}
      aria-invalid={error ? true : undefined}
      aria-describedby={describedBy(fid, error, help)}
      required={required}
      {...rest}
    />
  );
  return (
    <Field label={label} help={help} error={error} required={required} aside={aside} className={className} htmlFor={fid}>
      {icon ? (
        <div className="input-wrap">
          <Icon name={icon} />
          {input}
        </div>
      ) : (
        input
      )}
    </Field>
  );
});

type TextAreaFieldProps = FieldShellProps & Omit<TextareaHTMLAttributes<HTMLTextAreaElement>, 'className'> & { inputClassName?: string };

export const TextAreaField = forwardRef<HTMLTextAreaElement, TextAreaFieldProps>(function TextAreaField(
  { label, help, error, required, aside, className, inputClassName, id, ...rest },
  ref,
) {
  const auto = useId();
  const fid = id ?? auto;
  return (
    <Field label={label} help={help} error={error} required={required} aside={aside} className={className} htmlFor={fid}>
      <textarea
        ref={ref}
        id={fid}
        className={['textarea', inputClassName].filter(Boolean).join(' ')}
        aria-invalid={error ? true : undefined}
        aria-describedby={describedBy(fid, error, help)}
        required={required}
        {...rest}
      />
    </Field>
  );
});

type SelectFieldProps = FieldShellProps &
  Omit<SelectHTMLAttributes<HTMLSelectElement>, 'className'> & { options?: { value: string; label: string; disabled?: boolean }[]; inputClassName?: string };

export const SelectField = forwardRef<HTMLSelectElement, SelectFieldProps>(function SelectField(
  { label, help, error, required, aside, className, options, children, inputClassName, id, ...rest },
  ref,
) {
  const auto = useId();
  const fid = id ?? auto;
  return (
    <Field label={label} help={help} error={error} required={required} aside={aside} className={className} htmlFor={fid}>
      <select
        ref={ref}
        id={fid}
        className={['select', inputClassName].filter(Boolean).join(' ')}
        aria-invalid={error ? true : undefined}
        aria-describedby={describedBy(fid, error, help)}
        required={required}
        {...rest}
      >
        {options?.map((o) => (
          <option key={o.value} value={o.value} disabled={o.disabled}>
            {o.label}
          </option>
        ))}
        {children}
      </select>
    </Field>
  );
});

/** A search box with the magnifier inside. */
export const SearchInput = forwardRef<HTMLInputElement, InputHTMLAttributes<HTMLInputElement> & { label: string; wrapClassName?: string }>(
  function SearchInput({ label, className, wrapClassName, ...rest }, ref) {
    return (
      <div className={['input-wrap', wrapClassName].filter(Boolean).join(' ')}>
        <Icon name="search" />
        <input ref={ref} type="search" aria-label={label} className={['input', className].filter(Boolean).join(' ')} {...rest} />
      </div>
    );
  },
);
